import React, { useRef, useEffect, useState } from "react";
import TinyGradient from "tinygradient";
import { ChevronUp } from "lucide-react";
import { GradientColors } from "./constants";

export interface Chunk {
  start: number;
  end: number;
}

export interface GradientBarProps {
  name: string;
  value: number;
  minValue: number;
  maxValue: number;
  decimalPlaces: number;
  breakpoints: Chunk[];
  indexNames: string[];
  displayValues: Chunk[];
  reversed?: boolean;
}

// Simple luminance calculation
const getLuminance = (hex: string) => {
  const rgb = parseInt(hex.replace("#", ""), 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;

  // sRGB luminance
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

const needsHighContrast = (color: string) => {
  const luminance = getLuminance(color);
  return luminance < 0.5;
};

export const GradientBar: React.FC<GradientBarProps> = (props) => {
  const [width, setWidth] = useState(0);
  const [textWidth, setTextWidth] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setWidth(entry.contentRect.width);
        }
      });
      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  useEffect(() => {
    if (textRef.current) {
       setTextWidth(textRef.current.offsetWidth);
    }
  }, [props.value, props.minValue, props.maxValue]); // Recalculate when value changes

  const gradient = new TinyGradient(GradientColors);

  const foundIndex = props.displayValues.findIndex(
    (displayValue) =>
      displayValue.start <= props.value && props.value < displayValue.end
  );
  let index: number = foundIndex;
  if (index === -1) {
    index = props.breakpoints.length - 1;
  }
  if (props.value < props.minValue) {
    index = 0;
  }
  // Reverse the index for the label if needed.
  // const displayIndex = props.reversed
  //   ? props.indexNames.length - 1 - index
  //   : index;
  // const indexName = props.indexNames[displayIndex];

  const positionWithinIndex = Math.min(
    Math.max(
      (props.value - props.displayValues[index].start) /
        (props.displayValues[index].end - props.displayValues[index].start),
      props.reversed ? -0.09 : 0.0
    ),
    1
  );

  // Calculate the offset.
  // chunkWidths in px
  const chunkWidths = props.breakpoints.map(
    (chunk) =>
      (chunk.end - chunk.start) *
      (width - 4 * (props.breakpoints.length - 1))
  );

  const previousChunksWidth = chunkWidths
    .slice(0, index)
    .reduce((acc, w) => acc + w + 4, 0);

  const computedOffset =
    previousChunksWidth + positionWithinIndex * chunkWidths[index];
    
  const offset = props.reversed ? width - computedOffset : computedOffset;
  
  const arrowOffset = offset - 12; // Center ChevronUp (24px width) at 'offset'
  let textOffset = offset - textWidth / 2; // Center text at 'offset'

  // Adjust textOffset for edge cases
  if (!props.reversed) {
    if (positionWithinIndex >= 1.0 && index === props.breakpoints.length - 1) {
      textOffset -= 10;
    } else if (positionWithinIndex < 0.09 && index === 0) {
      textOffset += 10;
    }
  } else {
    if (positionWithinIndex >= 1.0 && index === props.breakpoints.length - 1) {
      textOffset += 10;
    } else if (positionWithinIndex < 0.09 && index === 0) {
      textOffset -= 10;
    }
  }

  let valueText = props.value.toFixed(props.decimalPlaces);
  if (props.value < props.minValue) {
    valueText = "<" + props.minValue.toFixed(props.decimalPlaces);
  } else if (props.value > props.maxValue) {
    valueText = ">" + props.maxValue.toFixed(props.decimalPlaces);
  }

  const minText = `${props.minValue <= 0 ? "" : "<"}${props.minValue.toFixed(
    props.decimalPlaces
  )}`;
  const maxText = `>${props.maxValue.toFixed(props.decimalPlaces)}`;

  // Hide main boundary labels when hovering edge bins (to avoid duplication)
  const lastBinIndex = props.breakpoints.length - 1;
  const hideLeftLabel = props.reversed
    ? hoveredIndex === lastBinIndex
    : hoveredIndex === 0;
  const hideRightLabel = props.reversed
    ? hoveredIndex === 0
    : hoveredIndex === lastBinIndex;

  return (
    <div className="mb-7 relative mt-8">
      {!hideLeftLabel && (
        <span className="absolute left-[-4px] top-[-24px] text-xs text-gray-400">
          {props.reversed ? maxText : minText}
        </span>
      )}
      {!hideRightLabel && (
        <span className="absolute right-[-4px] top-[-24px] text-xs text-gray-400">
          {props.reversed ? minText : maxText}
        </span>
      )}

      <div ref={containerRef} className="flex gap-1 h-5 w-full">
        {props.breakpoints.map((chunk, i) => {
          const labelIndex = props.reversed
            ? props.indexNames.length - 1 - i
            : i;
          const colorStart = gradient
            .rgbAt(Math.max(0, Math.min(1, chunk.start)))
            .toHexString();
          const colorEnd = gradient
            .rgbAt(Math.max(0, Math.min(1, chunk.end)))
            .toHexString();
          const centerColor = gradient
             .rgbAt(Math.max(0, Math.min(1, chunk.start + (chunk.end - chunk.start) / 2)))
             .toHexString();
          const textColor = needsHighContrast(centerColor) ? "#f3f4f6" : "#111827"; // gray-100 or gray-900

          const chunkStyle = {
             width: `${(chunk.end - chunk.start) * 100}%`, // Use percentage for width to be responsive
             background: `linear-gradient(90deg, ${colorStart}, ${colorEnd})`
          };

          const isHovered = hoveredIndex === i;
          const displayValue = props.displayValues[i];
          const isFirstBin = i === 0;
          const isLastBin = i === props.breakpoints.length - 1;
          const startValue = displayValue.start.toFixed(props.decimalPlaces);
          const endValue = displayValue.end.toFixed(props.decimalPlaces);

          // Determine left and right labels accounting for overflow bins and reversed
          // First bin (underflow): only show upper boundary with < prefix
          // Last bin (overflow): only show lower boundary with > prefix
          // Middle bins: show both boundaries
          let leftLabel: string | null = null;
          let rightLabel: string | null = null;

          if (props.reversed) {
            if (isLastBin) {
              // Overflow bin (on left when reversed): show >threshold on right only
              rightLabel = `>${endValue}`;
            } else if (isFirstBin) {
              // Underflow bin (on right when reversed): show <threshold on left only
              leftLabel = `<${startValue}`;
            } else {
              leftLabel = endValue;
              rightLabel = startValue;
            }
          } else {
            if (isFirstBin) {
              // Underflow bin: show <threshold on right only
              rightLabel = `<${endValue}`;
            } else if (isLastBin) {
              // Overflow bin: show >threshold on left only
              leftLabel = `>${startValue}`;
            } else {
              leftLabel = startValue;
              rightLabel = endValue;
            }
          }

          return (
            <div
              key={i}
              className="h-5 rounded flex items-center justify-center relative transition-transform duration-150 cursor-pointer"
              style={{
                ...chunkStyle,
                transform: isHovered ? 'scaleY(1.3)' : 'scaleY(1)',
              }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {isHovered && leftLabel && (
                <span
                  className="absolute left-0 bottom-full mb-1 text-xs text-muted-foreground whitespace-nowrap"
                  style={{ transform: 'scaleY(0.77)', transformOrigin: 'left' }}
                >
                  {leftLabel}
                </span>
              )}
              {isHovered && rightLabel && (
                <span
                  className="absolute right-0 bottom-full mb-1 text-xs text-muted-foreground whitespace-nowrap"
                  style={{ transform: 'scaleY(0.77)', transformOrigin: 'right' }}
                >
                  {rightLabel}
                </span>
              )}
              <span
                className="text-xs font-medium transition-transform duration-150"
                style={{
                  color: textColor,
                  transform: isHovered ? 'scaleY(0.77)' : 'scaleY(1)',
                }}
              >
                {props.indexNames[labelIndex]}
              </span>
            </div>
          );
        })}
      </div>

      <ChevronUp
        className="absolute w-6 h-6 text-foreground"
        style={{
            left: `${arrowOffset}px`,
            top: '20px',
        }}
      />
      <span
        ref={textRef}
        className="absolute text-xs font-medium text-foreground"
        style={{
            left: `${textOffset}px`,
            top: '36px',
        }}
      >
        {valueText}
      </span>
    </div>
  );
};
